import { useState, useEffect } from 'react';
import { open } from '../../tauriBridge';
import { useSimulationStore } from '../../stores/simulationStore';
import type { VesselConfig, VesselType, ManualWeather } from '../../types';
import FaultPanel from './FaultPanel';
import AisTargetPanel from './AisTargetPanel';

// Vessel configuration section
function VesselConfig() {
  const { setVesselConfig, fetchVesselConfig } = useSimulationStore();
  const [config, setConfig] = useState<VesselConfig>({
    name: 'Test Vessel',
    mmsi: null,
    vessel_type: 'powerboat',
    length_overall: 12.0,
    beam: 4.0,
    draft: 1.5,
    displacement: 8000,
    min_speed: 0,
    cruise_speed: 8.0,
    max_speed: 15.0,
    acceleration: 0.5,
    use_polars: false,
    performance_factor: 0.85,
    spinnaker_available: true,
    code_zero_available: false,
  });

  useEffect(() => {
    fetchVesselConfig().then(setConfig).catch(console.error);
  }, [fetchVesselConfig]);

  const handleChange = (field: keyof VesselConfig, value: any) => {
    const newConfig = { ...config, [field]: value };
    setConfig(newConfig);
    setVesselConfig(newConfig);
  };

  return (
    <div className="space-y-4">
      {/* Vessel Type */}
      <div>
        <label className="label">Vessel Type</label>
        <div className="flex space-x-2">
          {(['powerboat', 'sailboat'] as VesselType[]).map((type) => (
            <button
              key={type}
              className={`flex-1 py-2 px-3 rounded-md text-sm capitalize transition-colors ${
                config.vessel_type === type
                  ? 'bg-marine-600 text-white'
                  : 'bg-nautical-dark text-gray-300 hover:bg-nautical-panel'
              }`}
              onClick={() => handleChange('vessel_type', type)}
            >
              {type === 'powerboat' ? '🚤' : '⛵'} {type}
            </button>
          ))}
        </div>
      </div>

      {/* Vessel Name */}
      <div>
        <label className="label">Vessel Name</label>
        <input
          type="text"
          className="input"
          value={config.name}
          onChange={(e) => handleChange('name', e.target.value)}
        />
      </div>

      {/* Dimensions */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Length (m)</label>
          <input
            type="number"
            className="input"
            value={config.length_overall}
            onChange={(e) => handleChange('length_overall', parseFloat(e.target.value))}
          />
        </div>
        <div>
          <label className="label">Beam (m)</label>
          <input
            type="number"
            className="input"
            value={config.beam}
            onChange={(e) => handleChange('beam', parseFloat(e.target.value))}
          />
        </div>
        <div>
          <label className="label">Draft (m)</label>
          <input
            type="number"
            className="input"
            value={config.draft}
            onChange={(e) => handleChange('draft', parseFloat(e.target.value))}
          />
        </div>
        <div>
          <label className="label">Displacement (kg)</label>
          <input
            type="number"
            className="input"
            value={config.displacement}
            onChange={(e) => handleChange('displacement', parseFloat(e.target.value))}
          />
        </div>
      </div>

      {/* Speed */}
      <div>
        <label className="label">Speed Range (knots)</label>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <span className="text-xs text-gray-500">Min</span>
            <input
              type="number"
              className="input"
              value={config.min_speed}
              onChange={(e) => handleChange('min_speed', parseFloat(e.target.value))}
            />
          </div>
          <div>
            <span className="text-xs text-gray-500">Cruise</span>
            <input
              type="number"
              className="input"
              value={config.cruise_speed}
              onChange={(e) => handleChange('cruise_speed', parseFloat(e.target.value))}
            />
          </div>
          <div>
            <span className="text-xs text-gray-500">Max</span>
            <input
              type="number"
              className="input"
              value={config.max_speed}
              onChange={(e) => handleChange('max_speed', parseFloat(e.target.value))}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Sailing configuration section (only shown for sailboats)
function SailingConfig() {
  const { polarData, loadPolarFile, clearPolar } = useSimulationStore();
  const [usePolars, setUsePolars] = useState(false);
  const [performanceFactor, setPerformanceFactor] = useState(85);

  const handleImportPolar = async () => {
    try {
      const file = await open({
        multiple: false,
        filters: [
          {
            name: 'Polar Files',
            extensions: ['pol', 'csv', 'json', 'txt'],
          },
        ],
      });
      if (file) {
        await loadPolarFile(file as string);
      }
    } catch (err) {
      console.error('Failed to load polar file:', err);
    }
  };

  return (
    <div className="space-y-4">
      {/* Polar Data */}
      <div>
        <label className="label">Polar Data</label>
        {polarData ? (
          <div className="bg-nautical-dark rounded-lg p-3 border border-nautical-border">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-200">{polarData.info.name}</div>
                <div className="text-xs text-gray-500">
                  TWS: {polarData.info.tws_range[0]}-{polarData.info.tws_range[1]} kn
                </div>
              </div>
              <button
                className="text-xs text-red-400 hover:text-red-300"
                onClick={clearPolar}
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <button
            className="w-full py-2 px-3 rounded-md bg-nautical-dark border border-dashed border-nautical-border text-gray-400 hover:border-marine-500 hover:text-marine-400 transition-colors"
            onClick={handleImportPolar}
          >
            Import Polar File
          </button>
        )}
      </div>

      {/* Use Polars Toggle */}
      <div className="flex items-center justify-between">
        <label className="text-sm text-gray-300">Use polars for speed</label>
        <button
          className={`w-12 h-6 rounded-full transition-colors ${
            usePolars ? 'bg-marine-600' : 'bg-nautical-dark'
          }`}
          onClick={() => setUsePolars(!usePolars)}
        >
          <div
            className={`w-5 h-5 rounded-full bg-white transition-transform ${
              usePolars ? 'translate-x-6' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      {/* Performance Factor */}
      <div>
        <label className="label">Performance Factor: {performanceFactor}%</label>
        <input
          type="range"
          min="50"
          max="100"
          value={performanceFactor}
          onChange={(e) => setPerformanceFactor(parseInt(e.target.value))}
          className="w-full"
        />
      </div>

      {/* Sail Configuration */}
      <div>
        <label className="label">Available Sails</label>
        <div className="space-y-2">
          <label className="flex items-center space-x-2 text-sm text-gray-300">
            <input type="checkbox" defaultChecked className="rounded" />
            <span>Standard sails (Main + Jib/Genoa)</span>
          </label>
          <label className="flex items-center space-x-2 text-sm text-gray-300">
            <input type="checkbox" defaultChecked className="rounded" />
            <span>Spinnaker</span>
          </label>
          <label className="flex items-center space-x-2 text-sm text-gray-300">
            <input type="checkbox" className="rounded" />
            <span>Code 0</span>
          </label>
        </div>
      </div>
    </div>
  );
}

// Weather configuration section
function WeatherConfig() {
  const { manualWeather, setManualWeather, gribInfo, loadGribFile, useGrib, setUseGrib } =
    useSimulationStore();
  const [weather, setWeather] = useState<ManualWeather>(manualWeather);

  const handleChange = (field: keyof ManualWeather, value: number) => {
    const newWeather = { ...weather, [field]: value };
    setWeather(newWeather);
    setManualWeather(newWeather);
  };

  const handleImportGrib = async () => {
    try {
      const file = await open({
        multiple: false,
        filters: [
          {
            name: 'GRIB Files',
            extensions: ['grb', 'grb2', 'grib', 'grib2'],
          },
        ],
      });
      if (file) {
        await loadGribFile(file as string);
      }
    } catch (err) {
      console.error('Failed to load GRIB file:', err);
    }
  };

  return (
    <div className="space-y-4">
      {/* Weather Source */}
      <div>
        <label className="label">Weather Source</label>
        <div className="flex space-x-2">
          <button
            className={`flex-1 py-2 px-3 rounded-md text-sm transition-colors ${
              !useGrib
                ? 'bg-marine-600 text-white'
                : 'bg-nautical-dark text-gray-300 hover:bg-nautical-panel'
            }`}
            onClick={() => setUseGrib(false)}
          >
            Manual
          </button>
          <button
            className={`flex-1 py-2 px-3 rounded-md text-sm transition-colors ${
              useGrib
                ? 'bg-marine-600 text-white'
                : 'bg-nautical-dark text-gray-300 hover:bg-nautical-panel'
            }`}
            onClick={() => gribInfo && setUseGrib(true)}
            disabled={!gribInfo}
          >
            GRIB
          </button>
        </div>
      </div>

      {/* GRIB Data */}
      <div>
        <label className="label">GRIB Data</label>
        {gribInfo ? (
          <div className="bg-nautical-dark rounded-lg p-3 border border-nautical-border text-sm">
            <div className="text-gray-200">{gribInfo.model}</div>
            <div className="text-xs text-gray-500">
              {gribInfo.forecast_hours.length} time steps
            </div>
          </div>
        ) : (
          <button
            className="w-full py-2 px-3 rounded-md bg-nautical-dark border border-dashed border-nautical-border text-gray-400 hover:border-marine-500 hover:text-marine-400 transition-colors"
            onClick={handleImportGrib}
          >
            Import GRIB File
          </button>
        )}
      </div>

      {/* Manual Weather Settings */}
      {!useGrib && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Wind Speed (kn)</label>
              <input
                type="number"
                className="input"
                value={weather.wind_speed}
                onChange={(e) => handleChange('wind_speed', parseFloat(e.target.value))}
              />
            </div>
            <div>
              <label className="label">Wind Dir (°)</label>
              <input
                type="number"
                className="input"
                value={weather.wind_direction}
                onChange={(e) => handleChange('wind_direction', parseFloat(e.target.value))}
              />
            </div>
          </div>

          <div>
            <label className="label">
              Variability: {(weather.wind_variability * 100).toFixed(0)}%
            </label>
            <input
              type="range"
              min="0"
              max="50"
              value={weather.wind_variability * 100}
              onChange={(e) => handleChange('wind_variability', parseInt(e.target.value) / 100)}
              className="w-full"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Current (kn)</label>
              <input
                type="number"
                className="input"
                value={weather.current_speed}
                onChange={(e) => handleChange('current_speed', parseFloat(e.target.value))}
              />
            </div>
            <div>
              <label className="label">Current Dir (°)</label>
              <input
                type="number"
                className="input"
                value={weather.current_direction}
                onChange={(e) => handleChange('current_direction', parseFloat(e.target.value))}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Scenario management section
function ScenarioPanel() {
  const { scenarios, listScenarios, loadScenarioFile, saveScenarioFile, deleteScenario } = useSimulationStore();
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    listScenarios();
  }, [listScenarios]);

  const handleLoad = async (filename: string) => {
    setLoading(filename);
    await loadScenarioFile(filename);
    setLoading(null);
  };

  const handleDelete = async (filename: string) => {
    setDeleting(filename);
    await deleteScenario(filename);
    setDeleting(null);
  };

  const handleSave = async () => {
    if (!saveName.trim()) return;
    setSaving(true);
    const filename = saveName.endsWith('.json') ? saveName : `${saveName}.json`;
    await saveScenarioFile(filename, saveName);
    setSaveName('');
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      {/* Save Current */}
      <div>
        <label className="label">Save Current Setup</label>
        <div className="flex space-x-2">
          <input
            type="text"
            className="input flex-1"
            placeholder="scenario-name"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          />
          <button
            className="px-3 py-2 rounded-md bg-marine-600 text-white text-sm hover:bg-marine-500 disabled:opacity-50"
            onClick={handleSave}
            disabled={!saveName.trim() || saving}
          >
            {saving ? '...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Scenario List */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="label">Server Scenarios</label>
          <button
            className="text-xs text-marine-400 hover:text-marine-300"
            onClick={listScenarios}
          >
            Refresh
          </button>
        </div>
        {scenarios.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-4">No saved scenarios</div>
        ) : (
          <div className="space-y-1">
            {scenarios.map((s) => (
              <div
                key={s.filename}
                className="flex items-center justify-between px-3 py-2 rounded-md bg-nautical-dark border border-nautical-border"
              >
                <div className="flex-1 min-w-0 mr-2">
                  <div className="text-sm text-gray-200 truncate">{s.name}</div>
                  {s.description && (
                    <div className="text-xs text-gray-500 truncate">{s.description}</div>
                  )}
                  <div className="text-xs text-gray-600">{s.filename}</div>
                </div>
                <div className="flex items-center space-x-1 shrink-0">
                  <button
                    className="px-3 py-1 rounded bg-green-700 text-white text-xs hover:bg-green-600 disabled:opacity-50"
                    onClick={() => handleLoad(s.filename)}
                    disabled={loading !== null || deleting !== null}
                  >
                    {loading === s.filename ? '...' : 'Load'}
                  </button>
                  <button
                    className="px-2 py-1 rounded bg-red-800 text-white text-xs hover:bg-red-600 disabled:opacity-50"
                    onClick={() => handleDelete(s.filename)}
                    disabled={loading !== null || deleting !== null}
                    title={`Delete ${s.filename}`}
                  >
                    {deleting === s.filename ? '...' : 'X'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Main config panel
export default function ConfigPanel() {
  const { state, activeFaultCount, aisTargets } = useSimulationStore();
  const [expandedSection, setExpandedSection] = useState<string | null>('vessel');

  const isSailboat = state?.vessel?.config.vessel_type === 'sailboat';

  const faultTitle = activeFaultCount > 0
    ? `Faults (${activeFaultCount} active)`
    : 'Faults';

  const aisTitle = `AIS Targets (${aisTargets.length}/10)`;

  const sections = [
    { id: 'vessel', title: 'Vessel', component: <VesselConfig /> },
    ...(isSailboat
      ? [{ id: 'sailing', title: '⛵ Sailing', component: <SailingConfig /> }]
      : []),
    { id: 'weather', title: '🌊 Weather', component: <WeatherConfig /> },
    { id: 'faults', title: faultTitle, component: <FaultPanel />, alert: activeFaultCount > 0 },
    { id: 'ais', title: aisTitle, component: <AisTargetPanel /> },
    { id: 'scenarios', title: 'Scenarios', component: <ScenarioPanel /> },
  ];

  return (
    <div className="p-2">
      {sections.map((section) => (
        <div key={section.id} className="mb-2">
          <button
            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
              'alert' in section && section.alert
                ? 'bg-red-950/40 border-red-500/40 text-red-300 hover:bg-red-950/60'
                : 'bg-nautical-panel border-nautical-border text-gray-200 hover:bg-nautical-dark'
            }`}
            onClick={() =>
              setExpandedSection(expandedSection === section.id ? null : section.id)
            }
          >
            <span>{section.title}</span>
            <span
              className={`transition-transform ${
                expandedSection === section.id ? 'rotate-180' : ''
              }`}
            >
              ▼
            </span>
          </button>
          {expandedSection === section.id && (
            <div className="mt-2 p-4 bg-nautical-panel rounded-lg border border-nautical-border">
              {section.component}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
